import { CalendarClock, FilePlus2, Inbox, LayoutTemplate, Send, type LucideIcon } from "lucide-react";
import type { CSSProperties } from "react";

/**
 * The sections of the Messages workspace.
 *
 * `built` is deliberately part of the data rather than something the nav infers.
 * Three of these five have no server behind them yet — there is no scheduling,
 * no stored template and no inbox — and a nav that presented all five as equals
 * would send an administrator to a page that silently does nothing. Naming the
 * gap in the model means the nav can say so, and means turning one on later is a
 * one-word change here rather than a hunt through the view.
 */
export type MessageSectionId = "create" | "campaigns" | "scheduled" | "templates" | "inbox";

export type MessageSection = {
  id: MessageSectionId;
  label: string;
  icon: LucideIcon;
  built: boolean;
  /** What the section will hold, shown where it does not exist yet. */
  summary: string;
};

export const MESSAGE_SECTIONS: readonly MessageSection[] = [
  {
    id: "create",
    label: "Create a Message",
    icon: FilePlus2,
    built: true,
    summary: "Compose a consent-aware draft for one attendee, an event roster, or a segment.",
  },
  {
    id: "campaigns",
    label: "Campaigns",
    icon: Send,
    built: true,
    summary: "Drafts prepared for review, and what has been delivered.",
  },
  {
    id: "scheduled",
    label: "Scheduled",
    icon: CalendarClock,
    built: false,
    summary: "Messages queued to send at a chosen time, with the consent re-check happening at delivery rather than at drafting.",
  },
  {
    id: "templates",
    label: "Templates",
    icon: LayoutTemplate,
    built: false,
    summary: "Saved, reusable message bodies so a recurring announcement is not rewritten each time.",
  },
  {
    id: "inbox",
    label: "Inbox",
    icon: Inbox,
    built: false,
    summary: "Replies from recipients, threaded against the campaign that prompted them.",
  },
];

export default function MessageWorkspaceNav({
  active,
  onSelect,
}: {
  active: MessageSectionId;
  onSelect: (id: MessageSectionId) => void;
}) {
  return (
    <nav
      aria-label="Messages sections"
      style={{
        display: "grid",
        gap: ".25rem",
        alignContent: "start",
        padding: ".5rem",
        border: "1px solid rgba(255,255,255,.07)",
        borderRadius: ".85rem",
        background: "rgba(255,255,255,.025)",
      }}
    >
      {MESSAGE_SECTIONS.map(section => {
        const Icon = section.icon;
        const isActive = section.id === active;
        const style: CSSProperties = {
          display: "flex",
          alignItems: "center",
          gap: ".55rem",
          width: "100%",
          minHeight: 42,
          padding: ".5rem .65rem",
          border: isActive ? "1px solid rgba(233,30,140,.5)" : "1px solid transparent",
          borderRadius: ".6rem",
          background: isActive
            ? "linear-gradient(135deg,rgba(233,30,140,.22),rgba(125,234,255,.1))"
            : "transparent",
          color: isActive ? "#FFFFFF" : section.built ? "#B5C0D1" : "#71809A",
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "var(--chi-body-compact)",
          fontWeight: isActive ? 800 : 700,
          textAlign: "left",
          cursor: "pointer",
        };
        return (
          <button
            key={section.id}
            type="button"
            aria-current={isActive || undefined}
            onClick={() => onSelect(section.id)}
            style={style}
          >
            <Icon size={18} strokeWidth={1.9} aria-hidden="true" style={{ flex: "0 0 auto" }} />
            <span style={{ flex: 1 }}>{section.label}</span>
            {/*
              Marked rather than hidden or disabled. Hiding it would make the plan
              invisible, and disabling it would leave someone clicking a dead
              control with no explanation.
            */}
            {!section.built && (
              <span
                style={{
                  flex: "0 0 auto",
                  padding: ".1rem .35rem",
                  borderRadius: ".35rem",
                  border: "1px solid rgba(252,211,77,.35)",
                  background: "rgba(252,211,77,.1)",
                  color: "#FCD34D",
                  fontSize: "calc(var(--chi-body-compact) * 0.72)",
                  fontWeight: 700,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                }}
              >
                Soon
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
